import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaComments,
  FaFileUpload,
  FaInfoCircle,
  FaUserEdit,
  FaUserFriends,
  FaExclamationCircle,
  FaCheckCircle
} from "react-icons/fa";
import axios from "axios";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const WorkerHome = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(
    localStorage.getItem("workerStatus") || "available"
  );
  const [modalConfig, setModalConfig] = useState(null);
  const workerID = localStorage.getItem("workerID");

  const updateStatus = async (newStatus) => {
    try {
      await axios.post(`${config.API_BASE_URL}/worker/status`, {
        workerID,
        status: newStatus
      });

      localStorage.setItem("workerStatus", newStatus);
    } catch (err) {
      setModalConfig({
        type: "error",
        title: "Update Failed",
        message: "Failed to update your status."
      });

      setStatus(
        newStatus === "available"
          ? "unavailable"
          : "available"
      );
    }
  };

  const toggleAvailability = () => {
    const newStatus =
      status === "available"
        ? "unavailable"
        : "available";

    setStatus(newStatus);
    updateStatus(newStatus);
  };

  return (
    <WorkerSidebarShell activeItem="dashboard">
      <h1 style={headline}>Worker Dashboard</h1>

      <p style={subtitle}>
        Manage your profile and support residents.
      </p>

      {/* ACTION CARDS */}
      <div style={cards}>
        <button
          style={actionCard}
          onClick={() => navigate("/remoteProfileEdit")}
        >
          <FaUserEdit size={22} />
          <h3 style={cardTitle}>Edit Profile</h3>
        </button>

        <button
          style={actionCard}
          onClick={() => navigate("/activatedpatients")}
        >
          <FaUserFriends size={22} />
          <h3 style={cardTitle}>People I Support</h3>
        </button>

        <button
          style={actionCard}
          onClick={() => navigate("/activatedPatientMap")}
        >
          <FaFileUpload size={22} />
          <h3 style={cardTitle}>UberCare</h3>
        </button>

        <button
          style={actionCard}
          onClick={() => navigate("/worker-agents")}
        >
          <FaComments size={22} />
          <h3 style={cardTitle}>Agents</h3>
        </button>
      </div>

      {/* AVAILABILITY */}
      <div style={infoCard}>
        <div
          style={toggleContainer}
          onClick={toggleAvailability}
        >
          <div
            style={
              status === "available"
                ? toggleTrackActive
                : toggleTrack
            }
          >
            <div
              style={
                status === "available"
                  ? toggleThumbActive
                  : toggleThumb
              }
            />
          </div>

          <span
            style={
              status === "available"
                ? toggleLabelActive
                : toggleLabel
            }
          >
            {status === "available"
              ? "Available"
              : "Not Available"}
          </span>
        </div>

        <h3 style={cardTitle}>Availability</h3>
        <p style={cardText}>
          Tap to change your status.
        </p>
      </div>

      {/* INFO */}
      <div style={infoBox}>
        <h3 style={infoTitle}>
          <FaInfoCircle /> Service Notes
        </h3>

        <ul style={tips}>
          <li>Keep your profile updated.</li>
          <li>Update availability regularly.</li>
          <li>Review activated residents.</li>
        </ul>
      </div>

      {/* MODAL */}
      {modalConfig && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div
              style={
                modalConfig.type === "error"
                  ? errorIconWrapper
                  : successIconWrapper
              }
            >
              {modalConfig.type === "error" ? (
                <FaExclamationCircle
                  style={modalIconError}
                />
              ) : (
                <FaCheckCircle
                  style={modalIconSuccess}
                />
              )}
            </div>

            <h3 style={modalTitle}>
              {modalConfig.title}
            </h3>

            <p style={modalText}>
              {modalConfig.message}
            </p>

            <button
              style={
                modalConfig.type === "error"
                  ? confirmModalBtnError
                  : confirmModalBtnSuccess
              }
              onClick={() => setModalConfig(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </WorkerSidebarShell>
  );
};

export default WorkerHome;

const headline = {
  margin: 0,
  color: "#0E5580",
  fontSize: "32px",
  fontWeight: 700
};

const subtitle = {
  margin: "8px 0 24px",
  color: "#4f6475",
  fontSize: "16px"
};

const cards = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px",
  marginBottom: "28px"
};

const sharedCard = {
  background: "#fff",
  border: "1px solid #e3edf5",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 3px 12px rgba(15, 23, 42, 0.05)",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "10px",
  textAlign: "left"
};

const actionCard = {
  ...sharedCard,
  cursor: "pointer"
};

const infoCard = {
  ...sharedCard
};

const cardTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "18px",
  fontWeight: 600
};

const cardText = {
  margin: 0,
  color: "#4f6475",
  fontSize: "14px",
  lineHeight: 1.5
};

const infoBox = {
  background: "#fff",
  border: "1px solid #e3edf5",
  borderRadius: "12px",
  padding: "22px",
  boxShadow: "0 3px 12px rgba(15, 23, 42, 0.05)"
};

const infoTitle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  margin: "0 0 10px",
  color: "#0E5580",
  fontSize: "20px"
};

const tips = {
  margin: 0,
  paddingLeft: "18px",
  color: "#4f6475",
  lineHeight: 1.7
};

// Toggle Styles
const toggleContainer = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  padding: "4px 0",
  marginTop: "4px"
};

const toggleTrack = {
  width: "44px",
  height: "24px",
  background: "#cbd5e1",
  borderRadius: "12px",
  position: "relative",
  transition: "background 0.3s ease"
};

const toggleTrackActive = {
  ...toggleTrack,
  background: "#10b981" // green
};

const toggleThumb = {
  width: "18px",
  height: "18px",
  background: "#fff",
  borderRadius: "50%",
  position: "absolute",
  top: "3px",
  left: "3px",
  transition: "transform 0.3s ease",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
};

const toggleThumbActive = {
  ...toggleThumb,
  transform: "translateX(20px)"
};

const toggleLabel = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#64748b"
};

const toggleLabelActive = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#10b981"
};

// Modal Styles
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  animation: "fadeIn 0.2s ease-out"
};

const modalContent = {
  background: "#fff",
  borderRadius: "20px",
  padding: "32px",
  width: "90%",
  maxWidth: "400px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  animation: "slideUp 0.3s ease-out",
  textAlign: "center"
};

const errorIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#fef2f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const successIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#f0fdf4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const modalIconError = {
  fontSize: "24px",
  color: "#ef4444"
};

const modalIconSuccess = {
  fontSize: "24px",
  color: "#10b981"
};

const modalTitle = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#0f172a",
  margin: "0 0 12px 0"
};

const modalText = {
  fontSize: "15px",
  color: "#64748b",
  lineHeight: "1.5",
  margin: "0 0 24px 0"
};

const confirmModalBtnError = {
  width: "100%",
  padding: "12px",
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};

const confirmModalBtnSuccess = {
  width: "100%",
  padding: "12px",
  background: "#10b981",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};
